import moment from 'moment-timezone';
import MomentUtils from '@date-io/moment';

import 'moment/locale/uk';
import 'moment/locale/ru';

function getDaysName(daysCount, t) {
  return daysCount === 1 ? t('common.day') : t('common.days')
}

export const loadMomentLocales = () => {
  try {
    return true;
  } catch (error) {
    console.error('❌ Error loading moment locales:', error);
    return false;
  }
};

export const setMomentLocale = (language) => {
  let targetLocale = 'en';

  switch(language) {
    case 'uk_UA':
    case 'uk':
      targetLocale = 'uk';
      break;
    case 'ru_UA':
    case 'ru':
      targetLocale = 'ru';
      break;
    case 'en_US':
    case 'us_US':
    case 'en':
    default:
      targetLocale = 'en';
  }

  return moment.locale(targetLocale);
};

export const initializeMomentLocalization = () => {
  const localesLoaded = loadMomentLocales();

  if (localesLoaded) {
    setMomentLocale('en');
    patchMomentUtils();
    patchDatePickerGlobally();
    patchDatePickerViaDOM();
  }

  return localesLoaded;
};

let lastUpdateTime = 0;
let lastUpdateLanguage = null;

export const updateMomentLocale = (language) => {
  const now = Date.now();

  if (language === lastUpdateLanguage && now - lastUpdateTime < 1000) {
    return;
  }

  lastUpdateTime = now;
  lastUpdateLanguage = language;

  setMomentLocale(language);
  injectDatePickerLocalization(language);

  setTimeout(() => {
    forceRepatchExistingDatePickers(language);
  }, 100);

  setTimeout(() => {
    const currentDatePickers = document.querySelectorAll('.MuiPickersCalendarHeader-dayLabel');
    if (currentDatePickers.length > 0) {
      aggressiveDatePickerPatch(language);
    }
  }, 500);
};

export const LANGUAGE_LOCALE_MAP = {
  'uk_UA': 'uk',
  'uk': 'uk',
  'ru_UA': 'ru',
  'ru': 'ru',
  'en_US': 'en',
  'us_US': 'en',
  'en': 'en'
};

export const getLocaleByLanguage = (language) => {
  return LANGUAGE_LOCALE_MAP[language] || 'en';
};

export const isLanguageSupported = (language) => {
  return language in LANGUAGE_LOCALE_MAP;
};

let originalMomentUtils = null;

export const patchMomentUtils = () => {
  if (!originalMomentUtils && typeof window !== 'undefined') {
    originalMomentUtils = MomentUtils;

    class GlobalLocaleMomentUtils extends MomentUtils {
      constructor(options = {}) {
        super(options);
        this.locale = moment.locale();
      }

      date(value) {
        const date = super.date(value);
        if (date && typeof date.locale === 'function') {
          date.locale(moment.locale());
        }
        return date;
      }

      format(date, formatKey) {
        if (!date) return '';
        return date.clone().locale(moment.locale()).format(formatKey);
      }

      parse(value, format) {
        const date = super.parse(value, format);
        if (date && typeof date.locale === 'function') {
          date.locale(moment.locale());
        }
        return date;
      }

      startOfDay(date) {
        return date.clone().locale(moment.locale()).startOf('day');
      }

      endOfDay(date) {
        return date.clone().locale(moment.locale()).endOf('day');
      }

      startOfWeek(date) {
        return date.clone().locale(moment.locale()).startOf('week');
      }

      endOfWeek(date) {
        return date.clone().locale(moment.locale()).endOf('week');
      }

      startOfMonth(date) {
        return date.clone().locale(moment.locale()).startOf('month');
      }

      endOfMonth(date) {
        return date.clone().locale(moment.locale()).endOf('month');
      }

      getWeekdays() {
        return moment.weekdaysShort();
      }

      getWeekArray(date) {
        const start = this.startOfWeek(this.startOfMonth(date));
        const end = this.endOfWeek(this.endOfMonth(date));

        const weeks = [];
        let current = start.clone();

        while (current.isSameOrBefore(end, 'day')) {
          const week = [];
          for (let i = 0; i < 7; i++) {
            week.push(current.clone().locale(moment.locale()));
            current.add(1, 'day');
          }
          weeks.push(week);
        }

        return weeks;
      }
    }

    try {
      if (typeof window !== 'undefined' && window.MomentUtils) {
        window.MomentUtils = GlobalLocaleMomentUtils;
      }
    } catch (error) {
    }
  }
};

export const patchDatePickerGlobally = () => {
  try {
    if (typeof window !== 'undefined') {
      window.__CustomMomentUtils = class PatchedMomentUtils extends MomentUtils {
        constructor(options = {}) {
          super(options);
          this.locale = moment.locale();
        }

        date(value) {
          const date = super.date(value);
          if (date && typeof date.locale === 'function') {
            date.locale(moment.locale());
          }
          return date;
        }

        format(date, formatKey) {
          if (!date) return '';
          return date.clone().locale(moment.locale()).format(formatKey);
        }

        getWeekdays() {
          return moment.weekdaysShort();
        }

        parse(value, format) {
          const date = super.parse(value, format);
          if (date && typeof date.locale === 'function') {
            date.locale(moment.locale());
          }
          return date;
        }
      };
    }
  } catch (error) {
  }
};

export const patchDatePickerViaDOM = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const weekdayElements = node.querySelectorAll?.('[class*="MuiPickersCalendarHeader"], [class*="MuiDayPicker"] th');

          if (weekdayElements?.length > 0) {
            const currentWeekdays = moment.weekdaysShort();
            const englishWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

            weekdayElements.forEach((element, index) => {
              const text = element.textContent?.trim();
              const englishIndex = englishWeekdays.findIndex(day =>
                text?.toLowerCase().includes(day.toLowerCase())
              );

              if (englishIndex !== -1 && currentWeekdays[englishIndex]) {
                element.textContent = currentWeekdays[englishIndex];
              }
            });
          }

          const monthElements = node.querySelectorAll?.('[class*="MuiPickersCalendarHeader"] button, [class*="MuiPickersToolbar"] h4');
          monthElements?.forEach((element) => {
            const text = element.textContent?.trim();
            if (text) {
              for (let i = 0; i < 12; i++) {
                const englishMonth = moment().month(i).locale('en').format('MMMM');
                const localizedMonth = moment().month(i).locale(moment.locale()).format('MMMM');

                if (text.includes(englishMonth)) {
                  element.textContent = text.replace(englishMonth, localizedMonth);
                  break;
                }
              }
            }
          });
        }
      });
    });
  });

  if (window.datePickerDOMObserver) {
    window.datePickerDOMObserver.disconnect();
  }

  window.datePickerDOMObserver = observer;
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  return observer;
};

export const injectDatePickerLocalization = (language) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  document.body.setAttribute('data-language', language);

  let styleElement = document.getElementById('datepicker-localization');
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = 'datepicker-localization';
    document.head.appendChild(styleElement);
  }

  const weekdaysMap = {
    uk: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'],
    ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  };

  const monthsMap = {
    uk: ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
      'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'],
    ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
    en: ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December']
  };

  const weekdays = weekdaysMap[language] || weekdaysMap.en;
  const months = monthsMap[language] || monthsMap.en;

  const css = `
    .MuiPickersCalendarHeader-dayLabel {
      position: relative;
      overflow: hidden;
    }

    .MuiPickersCalendarHeader-dayLabel::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: inherit;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: inherit;
      font-weight: inherit;
      color: inherit;
    }

    .MuiPickersCalendarHeader-dayLabel:nth-child(1)::after { content: '${weekdays[0]}'; }
    .MuiPickersCalendarHeader-dayLabel:nth-child(2)::after { content: '${weekdays[1]}'; }
    .MuiPickersCalendarHeader-dayLabel:nth-child(3)::after { content: '${weekdays[2]}'; }
    .MuiPickersCalendarHeader-dayLabel:nth-child(4)::after { content: '${weekdays[3]}'; }
    .MuiPickersCalendarHeader-dayLabel:nth-child(5)::after { content: '${weekdays[4]}'; }
    .MuiPickersCalendarHeader-dayLabel:nth-child(6)::after { content: '${weekdays[5]}'; }
    .MuiPickersCalendarHeader-dayLabel:nth-child(7)::after { content: '${weekdays[6]}'; }

    .MuiPickersCalendarHeader-dayLabel {
      color: transparent !important;
    }
  `;

  styleElement.textContent = css;

  const patchDatePickerElements = () => {
    document.querySelectorAll('[class*="MuiPickersToolbar"] h4, [class*="MuiPickersCalendarHeader"] button').forEach(element => {
      if (element.dataset.patched) return;

      const originalText = element.textContent;
      if (originalText) {
        let newText = originalText;
        monthsMap.en.forEach((englishMonth, index) => {
          if (newText.includes(englishMonth)) {
            newText = newText.replace(englishMonth, months[index]);
          }
        });

        if (newText !== originalText) {
          element.textContent = newText;
          element.dataset.patched = 'true';
        }
      }
    });

    document.querySelectorAll('[class*="MuiPickersToolbar"] button').forEach(element => {
      if (element.textContent === 'Today' && !element.dataset.patched) {
        const translations = {
          uk: 'Сьогодні',
          ru: 'Сегодня',
          en: 'Today'
        };
        element.textContent = translations[language] || translations.en;
        element.dataset.patched = 'true';
      }
    });
  };

  patchDatePickerElements();

  if (window.datePickerCSSObserver) {
    window.datePickerCSSObserver.disconnect();
  }

  window.datePickerCSSObserver = new MutationObserver(() => {
    patchDatePickerElements();
  });

  window.datePickerCSSObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
};

export const forceRepatchExistingDatePickers = (language) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const weekdaysMap = {
    uk: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'],
    ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  };

  const monthsMap = {
    uk: ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
      'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'],
    ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
    en: ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December']
  };

  const weekdays = weekdaysMap[language] || weekdaysMap.en;
  const months = monthsMap[language] || monthsMap.en;

  document.querySelectorAll('.MuiPickersCalendarHeader-dayLabel').forEach((element, index) => {
    if (index < weekdays.length) {
      element.textContent = weekdays[index];
      element.setAttribute('data-localized', weekdays[index]);
    }
  });

  document.querySelectorAll('[class*="MuiPickersCalendarHeader"] [class*="dayLabel"]').forEach((element, index) => {
    if (index < weekdays.length) {
      element.textContent = weekdays[index];
    }
  });

  document.querySelectorAll('[class*="MuiPickersToolbar"] h4, [class*="MuiPickersCalendarHeader"] button').forEach(element => {
    let text = element.textContent;
    if (text) {
      monthsMap.en.forEach((englishMonth, index) => {
        if (text.includes(englishMonth)) {
          text = text.replace(englishMonth, months[index]);
        }
      });
      element.textContent = text;
    }
  });

  document.querySelectorAll('button').forEach(element => {
    if (element.textContent === 'Today') {
      const translations = {
        uk: 'Сьогодні',
        ru: 'Сегодня',
        en: 'Today'
      };
      element.textContent = translations[language] || translations.en;
    }
  });
};

export const aggressiveDatePickerPatch = (language) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const daySelectors = [
    '.MuiPickersCalendarHeader-dayLabel',
    '[class*="MuiPickersCalendarHeader"] [class*="dayLabel"]',
    '[class*="MuiDayPicker"] th',
    '[class*="MuiPickersCalendar"] [class*="weekDay"]',
    '[class*="PrivatePickersSlideTransition"] [class*="dayLabel"]'
  ];

  const weekdaysMap = {
    uk: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'],
    ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  };

  const weekdays = weekdaysMap[language] || weekdaysMap.en;

  daySelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach((element, index) => {
      if (index < weekdays.length) {
        element.textContent = weekdays[index];
      }
    });
  });

  forceRepatchExistingDatePickers(language);
};

export {
  getDaysName,
};
